import React, { useState, useEffect, useRef } from "react";
import {
  LOOPS_NEWSLETTER_FORM_ENDPOINT,
  LOOPS_DEFAULT_USER_GROUP,
} from "../constants/loops";

function LoopsPopup({
  buttonText = "Subscribe to Our Newsletter",
  className = "",
  autoShow = false,
  delay = 3000,
  source = "popup",
}) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMessage, setErrorMessage] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!autoShow) return;
    const timer = setTimeout(() => setOpen(true), delay);
    return () => clearTimeout(timer);
  }, [autoShow, delay]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    if (open) document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const close = () => {
    setOpen(false);
    // Let the close animation finish before resetting state
    setTimeout(() => {
      setStatus("idle");
      setEmail("");
      setErrorMessage("");
    }, 200);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setStatus("loading");
    setErrorMessage("");

    try {
      const body = new URLSearchParams();
      body.append("email", email);
      body.append("source", source);
      body.append("userGroup", LOOPS_DEFAULT_USER_GROUP);

      const response = await fetch(LOOPS_NEWSLETTER_FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      if (response.ok) {
        setStatus("success");
      } else {
        let message = "Something went wrong. Please try again.";
        try {
          const data = await response.json();
          if (data?.message) message = data.message;
        } catch {
          // ignore JSON parse errors — use default message
        }
        setErrorMessage(message);
        setStatus("error");
      }
    } catch {
      setErrorMessage("Network error. Please try again.");
      setStatus("error");
    }
  };

  return (
    <>
      {buttonText && (
        <button
          type="button"
          className={className}
          onClick={() => setOpen(true)}
        >
          {buttonText}
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={close}
          role="dialog"
          aria-modal="true"
          aria-labelledby="loops-popup-title"
        >
          <div
            className="relative w-full max-w-md rounded-lg bg-background p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              aria-label="Close"
              onClick={close}
              className="absolute right-3 top-3 text-2xl leading-none text-textSecondary hover:text-text"
            >
              ×
            </button>

            {status === "success" ? (
              <div className="text-center">
                <h4
                  id="loops-popup-title"
                  className="mb-3 text-2xl font-normal text-secondary"
                >
                  Thank you!
                </h4>
                <p className="text-sm text-textSecondary">
                  You have successfully joined our subscriber list.
                </p>
              </div>
            ) : (
              <>
                <h4
                  id="loops-popup-title"
                  className="mb-2 text-2xl font-normal text-secondary"
                >
                  Join Our Community
                </h4>
                <p className="mb-4 text-sm text-textSecondary">
                  Stay updated with wellness tips and special offers!
                </p>

                <form onSubmit={handleSubmit} noValidate>
                  <label htmlFor="loops-popup-email" className="sr-only">
                    Email
                  </label>
                  <input
                    ref={inputRef}
                    id="loops-popup-email"
                    type="email"
                    required
                    aria-required="true"
                    autoComplete="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mb-4 w-full rounded border border-tint bg-white px-3 py-2.5 text-sm text-text focus:border-primary/30 focus:ring-2 focus:ring-primary/30 focus:outline-none"
                  />

                  {status === "error" && errorMessage && (
                    <p className="mb-3 text-xs text-red-600">{errorMessage}</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="w-full rounded bg-accent hover:bg-accentDark py-2.5 text-sm font-bold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {status === "loading" ? "Subscribing..." : "Subscribe"}
                  </button>

                  <p className="mt-3 text-center text-xs text-textSecondary">
                    You can unsubscribe anytime.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default LoopsPopup;
